import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import {
  Github,
  Linkedin,
  Mail,
  ArrowDown,
  Download,
  Terminal,
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useIsMobile } from "./ui/use-mobile";

export function HomeSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const factor = prefersReduced || isMobile ? 0.4 : 1;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const { scrollYProgress: imageScrollProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [1, 0.5, 0],
  );

  // Intense parallax for the image
  const imageY = useTransform(
    imageScrollProgress,
    [0, 1],
    [-200 * factor, 200 * factor],
  );
  const imageScale = useTransform(
    imageScrollProgress,
    [0, 0.5, 1],
    [1 + 0.3 * factor, 1, 1 - 0.15 * factor],
  );
  // Start upright (0deg) and rotate slightly as user scrolls
  const imageRotate = useTransform(
    imageScrollProgress,
    [0, 1],
    [0, -6 * factor],
  );

  // Parallax for text elements
  const titleY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -150 * factor],
  );
  const subtitleY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -100 * factor],
  );
  const descY = useTransform(scrollYProgress, [0, 1], [0, -50 * factor]);

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ minHeight: "100vh" }}
    >
      <section
        className="min-h-screen sticky top-0 flex items-center justify-center bg-black text-[#eceff4] overflow-hidden industrial-grid"
        style={{ zIndex: 1 }}
      >
        <div className="scanline" />

        <motion.div
          style={{ scale, opacity }}
          className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-16 sm:py-20 md:py-24 lg:py-32 w-full relative"
        >
          {/* Coordinate labels */}
          <div className="absolute top-4 sm:top-6 md:top-8 left-4 sm:left-6 md:left-8 lg:left-12 mono text-xs text-[#d8dee9]/20">
            37°77'N / 122°42'W
          </div>
          <div className="absolute top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-8 lg:right-12 mono text-xs text-[#d8dee9]/20">
            SYS_ONLINE
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 md:gap-16 lg:gap-24 items-center relative">
            {/* Grid lines */}
            <div className="absolute inset-0 pointer-events-none z-0">
              <div className="absolute top-1/2 left-0 right-0 h-px bg-[#d8dee9]/5" />
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#d8dee9]/5" />
            </div>

            {/* Left side - Text */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8 md:space-y-10 relative z-10">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 1.2,
                  delay: 0.3,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                style={{ y: titleY }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <Terminal
                    className="w-4 h-4 text-[#d8dee9]/40"
                    strokeWidth={1.5}
                  />
                  <p className="mono text-xs tracking-[0.4em] uppercase text-[#d8dee9]/40">
                    SOFTWARE_ENGINEER
                  </p>
                </div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  className="text-[3rem] sm:text-[4rem] md:text-[5rem] lg:text-[6rem] xl:text-[8rem] leading-[0.85] mb-4 sm:mb-6 grotesk tracking-tighter text-left"
                  style={{
                    fontFamily: "var(--font-grotesk)",
                    fontWeight: 700,
                  }}
                >
                  Kathan
                  <br />
                  Sheth
                </motion.h1>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-px bg-[#eceff4]" />
                  <span className="mono text-xs text-[#e5e9f0]/60">
                    EST. 2004
                  </span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 1.2,
                  delay: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                style={{ y: subtitleY }}
                className="border-l-2 border-[#d8dee9]/20 pl-6"
              >
                <p className="text-base sm:text-lg text-neutral-400 max-w-lg leading-relaxed font-light">
                  Software Engineer @ <b className="text-[#eceff4]">PayPal</b>
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 1.2,
                  delay: 0.9,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                style={{ y: descY }}
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start sm:items-center pt-2 sm:pt-4"
              >
                <a
                  href="/Kathan Sheth - Resume.pdf"
                  className="group relative inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 py-3 sm:py-4 bg-black text-[#eceff4] hover:text-black border-2 border-[#d8dee9]/20 transition-all duration-500 overflow-hidden text-sm sm:text-base"
                >
                  <div className="absolute inset-0 bg-[#eceff4] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  <Download
                    className="w-4 h-4 relative z-10 transition-colors duration-500"
                    strokeWidth={2}
                  />
                  <span className="mono text-xs tracking-[0.3em] relative z-10 transition-colors duration-500">
                    Resume
                  </span>
                </a>

                <div className="flex items-center gap-4 sm:gap-6">
                  {[
                    {
                      icon: Github,
                      href: "https://github.com/kathansheth04",
                      label: "Github",
                    },
                    {
                      icon: Linkedin,
                      href: "https://www.linkedin.com/in/kathansheth",
                      label: "LinkedIn",
                    },
                    { icon: Mail, href: "mailto:sheth.kathan04@gmail.com", label: "Email" },
                  ].map((social, index) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.2 + index * 0.1 }}
                      className="p-2.5 sm:p-3 border border-[#d8dee9]/20 hover:border-[#eceff4] hover:bg-[#eceff4]/5 transition-all duration-300"
                      aria-label={social.label}
                    >
                      <social.icon
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        strokeWidth={1.5}
                      />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right side - Photo with intense parallax */}
            <motion.div
              ref={imageRef}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="lg:col-span-5 relative z-10"
            >
              {/* Frame corners */}
              <div className="absolute -top-2 sm:-top-4 -left-2 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-l-2 border-[#eceff4] z-20" />
              <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-r-2 border-[#eceff4] z-20" />
              <div className="absolute -bottom-2 sm:-bottom-4 -left-2 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-l-2 border-[#eceff4] z-20" />
              <div className="absolute -bottom-2 sm:-bottom-4 -right-2 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-r-2 border-[#eceff4] z-20" />

              <motion.div
                style={{
                  y: imageY,
                  scale: imageScale,
                  rotate: imageRotate,
                }}
                className="relative aspect-[3/4] overflow-hidden border-2 border-[#d8dee9]/20"
              >
                <ImageWithFallback
                  src="/profile.JPG"
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Label */}
              <div className="absolute -bottom-6 sm:-bottom-8 left-0 mono text-xs text-[#d8dee9]/40">
                MYPHOTO.JPG
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Intense parallax background elements */}
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, -600]),
            rotate: useTransform(
              scrollYProgress,
              [0, 1],
              [0, 90],
            ),
            opacity: useTransform(
              scrollYProgress,
              [0, 1],
              [0.1, 0],
            ),
          }}
          className="absolute top-0 right-0 w-[1000px] h-[1000px] border-2 border-[#d8dee9]/10 translate-x-1/2 -translate-y-1/2 pointer-events-none"
        />
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, 500]),
            rotate: useTransform(
              scrollYProgress,
              [0, 1],
              [0, -60],
            ),
            opacity: useTransform(
              scrollYProgress,
              [0, 1],
              [0.1, 0],
            ),
          }}
          className="absolute bottom-0 left-0 w-[800px] h-[800px] border-2 border-[#d8dee9]/10 -translate-x-1/2 translate-y-1/2 pointer-events-none"
        />

        {/* Additional geometric elements */}
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, 300]),
            x: useTransform(scrollYProgress, [0, 1], [0, -200]),
          }}
          className="absolute top-1/4 right-1/4 w-px h-96 bg-[#d8dee9]/5 pointer-events-none"
        />

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1,
            delay: 2,
            repeat: Infinity,
            repeatType: "reverse",
            repeatDelay: 1,
          }}
          className="absolute bottom-6 sm:bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="mono text-xs text-[#d8dee9]/20 tracking-widest">
            SCROLL
          </span>
          <ArrowDown
            className="w-4 h-4 text-[#d8dee9]/20"
            strokeWidth={1}
          />
        </motion.div>
      </section>
    </div>
  );
}